import React, { useMemo } from 'react';
import {
  PapiDeviation,
  PapiUser,
  PapiGallection,
  PapiGroup,
} from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { getSuggestionReasonsByPriority } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationSuggestionReasons } from '@wix/da-shared-react/pkg/types/deviation';
import { BrowseItem } from '../../../types/api';
import { ItemTypes } from '../../../constants';
import { showLessLikeThis } from '../../actions/recommendation';
import CollectionCard from './CollectionCard';
import MiniGroupCard from './MiniGroupCard';
import MiniArtistCard from './MiniArtistCard';
import DeviationCard from './DeviationCard';

import s from './ItemCard.scss';

const suggestionReasonToNameMap = {
  [DeviationSuggestionReasons.DailyDeviation]: 'daily_deviation',
  [DeviationSuggestionReasons.NewDeviant]: 'new_deviant',
  [DeviationSuggestionReasons.Popular]: 'popular',
  [DeviationSuggestionReasons.Recommended]: 'recommended',
  [DeviationSuggestionReasons.WatchingGroup]: 'watching_group',
  [DeviationSuggestionReasons.WatchingTopic]: 'watching_topic',
  [DeviationSuggestionReasons.WatchingUser]: 'watching_user',
  [DeviationSuggestionReasons.Boosted]: 'badge_boosted',
};

export interface Props {
  type: ItemTypes;
  item: BrowseItem;
  itemIndex: number;
  width: number;
  height: number;
  style?: React.CSSProperties;
  canShowLess?: boolean;
  disableShowLess?: boolean;
  inclusiveFooter?: boolean;
  showTierPromotionTooltip?: boolean;
  responsive?: boolean;
  allowMobileCard?: boolean;
  dataHook?: string;
  onShowLess: typeof showLessLikeThis;
  onRemove?: (deviation: PapiDeviation, offset: number) => void;
}

const ItemCard: React.FC<Props> = ({
  type,
  item,
  itemIndex,
  style = {},
  inclusiveFooter,
  showTierPromotionTooltip,
  responsive,
  width,
  height,
  dataHook,
  canShowLess,
  allowMobileCard,
  onRemove,
  onShowLess,
}) => {
  const reasonShown = useMemo(() => {
    if (type !== ItemTypes.Deviation) {
      return;
    }

    const reasons = getSuggestionReasonsByPriority(item as PapiDeviation);
    return reasons
      ?.map(reason => suggestionReasonToNameMap[reason] ?? 'unknown')
      .join(',');
  }, [item, type]);

  let result: JSX.Element;
  switch (type) {
    case ItemTypes.Deviation:
      const deviation = item as PapiDeviation;
      result = (
        <DeviationCard
          deviation={deviation}
          offset={itemIndex}
          width={width}
          height={height}
          style={style}
          canShowLess={canShowLess}
          responsive={responsive}
          reasonShown={reasonShown}
          allowMobileCard={allowMobileCard}
          onRemove={onRemove}
          onShowLess={onShowLess}
          showTierPromotionTooltip={showTierPromotionTooltip}
        />
      );
      break;
    case ItemTypes.User:
      result = (
        <MiniArtistCard
          user={item as PapiUser}
          style={style}
          dataHook={dataHook}
        />
      );
      break;
    case ItemTypes.Gallection:
      result = (
        <CollectionCard
          gallection={item as PapiGallection}
          width={width}
          height={height}
          style={style}
          inclusiveFooter={inclusiveFooter}
        />
      );
      break;
    case ItemTypes.Group:
      result = (
        <MiniGroupCard
          className={s['group-card']}
          style={style}
          group={item as PapiGroup}
          dataHook={dataHook}
        />
      );
      break;
    default:
      return null;
  }

  return (
    <BiLoggerContextProvider
      value={{ _rankIndex: itemIndex, reason_shown: reasonShown }}
    >
      {result}
    </BiLoggerContextProvider>
  );
};
ItemCard.displayName = 'ItemCard';

export default ItemCard;
